import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Plus,
  ArrowDown,
  ArrowDownUp,
  ArrowUp,
  ChevronDown,
  ChevronRight,
  MoreVertical,
  Building,
  Mail,
  Phone,
  Edit,
  Eye,
  RefreshCw,
  Link2,
  Trash2,
  RotateCcw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Card } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { ProviderLayout } from '@/components/layouts/provider-layout';
import { CreateCustomerDialog } from '@/components/dialogs/CreateCustomerDialog';
import { EditCustomerDialog } from '@/components/dialogs/EditCustomerDialog';
import { CustomerCheckoutLinksDialog } from '@/components/dialogs/CustomerCheckoutLinksDialog';
import { useDebounce } from '@/hooks/useDebounce';
import {
  useDeleteProviderCustomer,
  useProviderCustomers,
  useProviderDeletedCustomers,
  useRestoreProviderCustomer,
} from '@/hooks/provider/useProviderCustomers';
import { useProviderTeams } from '@/hooks/provider/useProviderTeams';
import {
  ProviderCustomer,
  ProviderCustomerStatus,
  ProviderCustomerStatusOption,
  ProviderCustomerDeleteBlockedMeta,
  ProviderCustomerDeleteMode,
  ProviderCustomerListParams,
} from '@/types/provider-customers';
import { ProviderTeam } from '@/types/provider-teams';
import { toast } from 'sonner';
import { extractJsonApiErrorCode, extractJsonApiErrorMessage } from '@/lib/jsonapi/errors';

const ROLE_OPTIONS: Array<{ value: 'all' | 'owner' | 'admin' | 'member' | 'individual'; labelKey: string }> = [
  { value: 'all', labelKey: 'customers.providerList.filters.allRoles' },
  { value: 'owner', labelKey: 'customers.providerList.filters.owner' },
  { value: 'admin', labelKey: 'customers.providerList.filters.admin' },
  { value: 'member', labelKey: 'customers.providerList.filters.member' },
  { value: 'individual', labelKey: 'customers.providerList.filters.individual' },
];

const FALLBACK_STATUS_OPTIONS: Array<ProviderCustomerStatusOption & { labelKey: string }> = [
  { value: '', label: '', labelKey: 'customers.providerList.statusFallback.all' },
  { value: 'active', label: '', labelKey: 'customers.providerList.statusFallback.active' },
  { value: 'inactive', label: '', labelKey: 'customers.providerList.statusFallback.inactive' },
  { value: 'trial', label: '', labelKey: 'customers.providerList.statusFallback.trial' },
  { value: 'suspended', label: '', labelKey: 'customers.providerList.statusFallback.suspended' },
  { value: 'pending_verification', label: '', labelKey: 'customers.providerList.statusFallback.pendingVerification' },
];

const STATUS_BADGE_VARIANTS: Record<ProviderCustomerStatus, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; className?: string }> = {
  active: { variant: 'default', className: 'bg-green-100 text-green-800' },
  inactive: { variant: 'outline', className: 'text-muted-foreground' },
  trial: { variant: 'secondary', className: 'bg-yellow-100 text-yellow-800' },
  suspended: { variant: 'destructive', className: 'bg-red-100 text-red-800' },
  pending_verification: { variant: 'secondary', className: 'bg-blue-100 text-blue-800' },
};

function formatDate(value: string | null | undefined) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString();
  } catch (error) {
    console.error('Failed to format date', error);
    return value;
  }
}

function formatUtcDateTime(value: string | null | undefined) {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return `${new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  }).format(parsed)} UTC`;
}

function mapToEditableCustomer(customer: ProviderCustomer) {
  const [firstName, ...rest] = customer.name.split(' ');
  const lastName = rest.join(' ');
  const hasMemberRole = customer.teamRoles.some((role) => role.role === 'member');

  let status: 'active' | 'inactive' | 'suspended';
  switch (customer.status) {
    case 'inactive':
      status = 'inactive';
      break;
    case 'suspended':
      status = 'suspended';
      break;
    default:
      status = 'active';
      break;
  }

  return {
    id: customer.id,
    firstName: firstName || customer.name,
    lastName: lastName || customer.name.split(' ').slice(1).join(' '),
    email: customer.email,
    company: customer.companyName ?? '',
    phone: customer.contactPhone ?? '',
    timezone: 'UTC',
    status,
    role: customer.isTeamOwner ? 'owner' : hasMemberRole ? 'member' : 'individual',
    twoFactorEnabled: false,
  } as const;
}

function getStatusBadge(status: ProviderCustomerStatus, label: string) {
  const { variant, className } = STATUS_BADGE_VARIANTS[status] ?? STATUS_BADGE_VARIANTS.inactive;
  return (
    <Badge variant={variant} className={className}>
      {label}
    </Badge>
  );
}

function renderTeamRoles(customer: ProviderCustomer) {
  return (
    <div className="space-y-1">
      {customer.teamRoles.map((role) => (
        <div key={`${customer.id}-${role.role}-${role.teamId ?? 'individual'}`} className="flex items-center gap-1 text-xs">
          {/* role.display already includes the icon; avoid rendering twice */}
          <span>{role.display}</span>
        </div>
      ))}
    </div>
  );
}

function getTrialCell(customer: ProviderCustomer, t: ReturnType<typeof useTranslation>['t']) {
  const hasTrialInfo =
    typeof customer.trialAssignedDays === 'number' || typeof customer.trialRemainingDays === 'number';

  if (!customer.isTrial && !hasTrialInfo) {
    return <span className="text-sm text-muted-foreground">—</span>;
  }

  if (hasTrialInfo) {
    const assigned = customer.trialAssignedDays ?? 0;
    const remaining = customer.trialRemainingDays ?? 0;
    return (
      <div className="text-sm">
        <div className="font-medium text-amber-600">
          {t('customers.providerList.trialDays', { count: assigned })}
        </div>
        <div className="text-muted-foreground">
          {t('customers.providerList.trialRemaining', { count: remaining })}
        </div>
      </div>
    );
  }

  return (
    <div className="text-sm">
      <div className="font-medium text-amber-600">{t('customers.providerList.trial')}</div>
      <div className="text-muted-foreground">
        {t('customers.providerList.trialEnds', { date: formatDate(customer.trialEndsAt) })}
      </div>
    </div>
  );
}

function ExpandedRow({ customer, t }: { customer: ProviderCustomer; t: ReturnType<typeof useTranslation>['t'] }) {
  return (
    <TableRow>
      <TableCell colSpan={7} className="bg-muted/30 p-4">
        <div className="space-y-4">
          <div>
            <h4 className="font-semibold text-sm mb-2">{t('customers.providerList.teamsAndRoles')}</h4>
            <div className="space-y-1 ml-1">
              {customer.teamRoles.map((role) => (
                <div key={`${customer.id}-detail-${role.role}-${role.teamId ?? 'individual'}`} className="text-sm">
                  {role.display}
                  {role.status ? <span className="text-muted-foreground"> · {role.status}</span> : null}
                  {role.joinedAt ? (
                    <span className="text-muted-foreground">
                      {' '}
                      · {t('customers.providerList.joined', { date: formatDate(role.joinedAt) })}
                    </span>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
          <div className="text-sm text-muted-foreground">
            {t('customers.providerList.created', { date: formatDate(customer.createdAt) })} ·{' '}
            {t('customers.providerList.updated', { date: formatDate(customer.updatedAt) })}
          </div>
        </div>
      </TableCell>
    </TableRow>
  );
}

export default function CustomersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | 'owner' | 'admin' | 'member' | 'individual'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | ProviderCustomerStatus>('all');
  const [teamFilter, setTeamFilter] = useState('all');
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<ProviderCustomer | null>(null);
  const [checkoutLinksDialogOpen, setCheckoutLinksDialogOpen] = useState(false);
  const [checkoutLinksCustomer, setCheckoutLinksCustomer] = useState<ProviderCustomer | null>(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [createdSort, setCreatedSort] = useState<'created_at' | '-created_at' | undefined>('-created_at');
  const [trialSort, setTrialSort] = useState<'trial_remaining_days' | '-trial_remaining_days' | undefined>(undefined);
  const [viewMode, setViewMode] = useState<'active' | 'deleted'>('active');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ProviderCustomer | null>(null);
  const [restoreTarget, setRestoreTarget] = useState<ProviderCustomer | null>(null);
  const [deleteMode, setDeleteMode] = useState<ProviderCustomerDeleteMode>('soft');
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteBlockedMeta, setDeleteBlockedMeta] = useState<ProviderCustomerDeleteBlockedMeta | null>(null);

  const debouncedSearch = useDebounce(searchQuery, 300);

  const sortParam = createdSort ?? trialSort;
  const listParams: ProviderCustomerListParams = {
    search: debouncedSearch || undefined,
    teamRole: roleFilter === 'all' ? undefined : roleFilter,
    status: statusFilter === 'all' ? undefined : (statusFilter as ProviderCustomerStatus),
    teamId: teamFilter === 'all' ? undefined : teamFilter,
    sort: sortParam,
    page,
    perPage,
  };

  const activeQuery = useProviderCustomers(listParams);
  const deletedQuery = useProviderDeletedCustomers(listParams);
  const deleteMutation = useDeleteProviderCustomer();
  const restoreMutation = useRestoreProviderCustomer();

  const isDeletedView = viewMode === 'deleted';
  const selectedQuery = isDeletedView ? deletedQuery : activeQuery;
  const customersData = selectedQuery.data;
  const isLoading = selectedQuery.isLoading;
  const isFetching = selectedQuery.isFetching;
  const error = selectedQuery.error;
  const refetch = selectedQuery.refetch;

  const { data: teamsData, isLoading: teamsLoading } = useProviderTeams();

  const customers = customersData?.customers ?? ([] as ProviderCustomer[]);

  const totalCustomers = customersData?.meta?.total ?? customers.length;
  const teamOwners = customers.filter((customer: ProviderCustomer) => customer.teamRoles.some((role) => role.role === 'owner')).length;
  const teamMembers = customers.filter((customer: ProviderCustomer) => customer.teamRoles.some((role) => role.role === 'member')).length;
  const individuals = customers.filter((customer: ProviderCustomer) => customer.teamRoles.every((role) => role.role === 'individual')).length;
  const trialing = customers.filter((customer: ProviderCustomer) => customer.isTrial).length;
  const uniqueTeams = new Set(
    customers.flatMap((customer: ProviderCustomer) => customer.teamRoles.map((role) => role.teamId).filter((teamId): teamId is string => Boolean(teamId)))
  ).size;

  const stats = {
    totalCustomers,
    teamOwners,
    teamMembers,
    individuals,
    trialing,
    uniqueTeams,
  };

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, roleFilter, statusFilter, teamFilter, createdSort, trialSort]);

  const paginationMeta = customersData?.meta;
  const currentPage = paginationMeta?.currentPage ?? page;
  const lastPage = paginationMeta?.lastPage ?? 1;
  const totalCount = paginationMeta?.total ?? customers.length;
  const from = paginationMeta?.from ?? (customers.length ? (currentPage - 1) * perPage + 1 : 0);
  const to =
    paginationMeta?.to ?? (customers.length ? Math.min(currentPage * perPage, totalCount) : 0);

  useEffect(() => {
    if (currentPage > lastPage) {
      setPage(Math.max(1, lastPage));
    }
  }, [currentPage, lastPage]);

  const roleOptions = useMemo(
    () =>
      ROLE_OPTIONS.map((option) => ({
        value: option.value,
        label: t(option.labelKey),
      })),
    [t],
  );

  const normalizedStatusOptions = useMemo(() => {
    const baseOptions =
      (customersData?.statusOptions as Array<ProviderCustomerStatusOption & { labelKey?: string }>) ??
      FALLBACK_STATUS_OPTIONS;

    return baseOptions.map((option) => {
      const value = option.value && option.value.length > 0 ? option.value : 'all';
      const key =
        (option as { labelKey?: string }).labelKey ||
        `customers.providerList.statusFallback.${value === 'all' ? 'all' : option.value}`;
      const label =
        option.label && option.label.length > 0
          ? option.label
          : t(key as string, {
              defaultValue: option.label || value,
            });
      return { value, label };
    });
  }, [customersData?.statusOptions, t]);

  const teamOptions = useMemo(() => {
    const list = teamsData ?? ([] as ProviderTeam[]);
    return [
      { value: 'all', label: t('customers.providerList.filters.allTeams') },
      ...list.map((team) => ({ value: team.id, label: team.name })),
    ];
  }, [teamsData, t]);

  const toggleRowExpansion = (customerId: string) => {
    setExpandedRows((prev) =>
      prev.includes(customerId) ? prev.filter((id) => id !== customerId) : [...prev, customerId]
    );
  };

  const handleEdit = (customer: ProviderCustomer) => {
    setSelectedCustomer(customer);
    setEditDialogOpen(true);
  };

  const handleCheckoutLinks = (customer: ProviderCustomer) => {
    setCheckoutLinksCustomer(customer);
    setCheckoutLinksDialogOpen(true);
  };

  const resolveHttpStatus = (err: unknown): number | null => {
    if (!err || typeof err !== 'object') return null;
    const topLevelStatus = (err as { status?: unknown }).status;
    if (typeof topLevelStatus === 'number') return topLevelStatus;
    const responseStatus = (err as { response?: { status?: unknown } }).response?.status;
    if (typeof responseStatus === 'number') return responseStatus;
    return null;
  };

  const parseDeleteBlockedMeta = (err: unknown): ProviderCustomerDeleteBlockedMeta | null => {
    if (!err || typeof err !== 'object') return null;
    const response = (err as { response?: { data?: unknown } }).response;
    const payload = response?.data;
    if (!payload || typeof payload !== 'object') return null;

    const meta = (payload as { meta?: unknown }).meta;
    if (!meta || typeof meta !== 'object') return null;

    const metaRecord = meta as Record<string, unknown>;
    const subscriptionRaw = metaRecord.subscription;
    const subscription =
      subscriptionRaw && typeof subscriptionRaw === 'object'
        ? (subscriptionRaw as Record<string, unknown>)
        : null;

    const allowedDeleteModes = Array.isArray(metaRecord.allowed_delete_modes)
      ? metaRecord.allowed_delete_modes
          .filter((item): item is string => typeof item === 'string')
          .filter((item): item is ProviderCustomerDeleteMode => item === 'soft' || item === 'hard')
      : [];

    return {
      subscription: subscription
        ? {
            subscriptionId:
              typeof subscription.subscription_id === 'string' ? subscription.subscription_id : null,
            id: typeof subscription.id === 'string' ? subscription.id : null,
            status: typeof subscription.status === 'string' ? subscription.status : null,
            planName: typeof subscription.plan_name === 'string' ? subscription.plan_name : null,
            currentPeriodEnd:
              typeof subscription.current_period_end === 'string' ? subscription.current_period_end : null,
            cancelAtPeriodEnd:
              typeof subscription.cancel_at_period_end === 'boolean'
                ? subscription.cancel_at_period_end
                : null,
            requiresManualCancel:
              typeof subscription.requires_manual_cancel === 'boolean'
                ? subscription.requires_manual_cancel
                : null,
          }
        : null,
      allowedDeleteModes,
      requiresConfirmation: Boolean(metaRecord.requires_confirmation),
    };
  };

  const openDeleteDialog = (customer: ProviderCustomer) => {
    setDeleteTarget(customer);
    setDeleteMode('soft');
    setDeleteConfirm('');
    setDeleteReason('');
    setDeleteBlockedMeta(null);
    setDeleteDialogOpen(true);
  };

  const openRestoreDialog = (customer: ProviderCustomer) => {
    setRestoreTarget(customer);
    setRestoreDialogOpen(true);
  };

  const handleDeleteCustomer = async () => {
    if (!deleteTarget) return;

    if (deleteMode === 'hard') {
      const expected = deleteTarget.email.trim().toLowerCase();
      const provided = deleteConfirm.trim().toLowerCase();
      if (!provided || provided !== expected) {
        toast.error(
          t(
            'customers.providerList.delete.confirmMismatch',
            'Please type the exact customer email to confirm permanent deletion.',
          ),
        );
        return;
      }
    }

    try {
      const result = await deleteMutation.mutateAsync({
        id: deleteTarget.id,
        mode: deleteMode,
        confirm: deleteMode === 'hard' ? deleteConfirm.trim() : undefined,
        reason: deleteReason.trim() || undefined,
      });
      toast.success(result.message || t('customers.providerList.delete.success', 'Customer deleted successfully.'));
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      setDeleteBlockedMeta(null);
      setExpandedRows((prev) => prev.filter((id) => id !== deleteTarget.id));
    } catch (mutationError) {
      const status = resolveHttpStatus(mutationError);
      const errorCode = extractJsonApiErrorCode(mutationError);
      if (status === 409 && errorCode === 'CUSTOMER_HAS_ACTIVE_SUBSCRIPTION') {
        const blockedMeta = parseDeleteBlockedMeta(mutationError);
        setDeleteBlockedMeta(blockedMeta);
        toast.error(
          extractJsonApiErrorMessage(
            mutationError,
            t(
              'customers.providerList.delete.activeSubscriptionBlocked',
              'Cancel or terminate the active subscription before permanent deletion.',
            ),
          ),
        );
        return;
      }
      if (status === 403) {
        toast.error(t('customers.providerList.errors.forbidden', 'You are not allowed to perform this action.'));
        return;
      }
      if (status === 422) {
        toast.error(
          t(
            'customers.providerList.delete.confirmMismatch',
            'Please type the exact customer email to confirm permanent deletion.',
          ),
        );
        return;
      }
      if (status === 404) {
        toast.error(t('customers.providerList.errors.notFound', 'Customer was not found for this provider.'));
        return;
      }
      if (status === 401) {
        toast.error(t('customers.providerList.errors.unauthorized', 'Your session expired. Please sign in again.'));
        return;
      }
      const message = mutationError instanceof Error ? mutationError.message : t('customers.providerList.delete.error', 'Unable to delete customer.');
      toast.error(message);
    }
  };

  const handleRestoreCustomer = async () => {
    if (!restoreTarget) return;
    try {
      await restoreMutation.mutateAsync(restoreTarget.id);
      toast.success(t('customers.providerList.restore.success', 'Customer restored successfully.'));
      setRestoreDialogOpen(false);
      setRestoreTarget(null);
      setExpandedRows((prev) => prev.filter((id) => id !== restoreTarget.id));
    } catch (mutationError) {
      const status = resolveHttpStatus(mutationError);
      if (status === 403) {
        toast.error(t('customers.providerList.errors.forbidden', 'You are not allowed to perform this action.'));
        return;
      }
      if (status === 404) {
        toast.error(t('customers.providerList.errors.notFound', 'Customer was not found for this provider.'));
        return;
      }
      if (status === 401) {
        toast.error(t('customers.providerList.errors.unauthorized', 'Your session expired. Please sign in again.'));
        return;
      }
      const message = mutationError instanceof Error ? mutationError.message : t('customers.providerList.restore.error', 'Unable to restore customer.');
      toast.error(message);
    }
  };

  const renderTableBody = () => {
    if (isLoading) {
      return (
        <TableRow>
          <TableCell colSpan={7}>
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, index) => (
                <Skeleton key={index} className="h-12 w-full" />
              ))}
            </div>
          </TableCell>
        </TableRow>
      );
    }

    if (error) {
      const message =
        error instanceof Error
          ? error.message
          : t('errors.failedToLoadCustomers', 'Failed to load customers');
      return (
        <TableRow>
          <TableCell colSpan={7}>
            <Alert variant="destructive">
              <AlertDescription className="flex items-center justify-between">
                <span>{message}</span>
                <Button variant="outline" size="sm" onClick={() => refetch()}>
                  {t('customers.providerList.retry')}
                </Button>
              </AlertDescription>
            </Alert>
          </TableCell>
        </TableRow>
      );
    }

    if (customers.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={7}>
            <div className="py-10 text-center">
              <p className="text-sm text-muted-foreground">
                {isDeletedView
                  ? t('customers.providerList.deleted.empty', 'No archived customers found.')
                  : t('customers.providerList.empty')}
              </p>
            </div>
          </TableCell>
        </TableRow>
      );
    }

    return customers.map((customer: ProviderCustomer) => {
      const statusLabel =
        customer.statusLabel && customer.statusLabel.length > 0
          ? customer.statusLabel
          : t(`customers.providerList.statusFallback.${customer.status}`, {
              defaultValue: customer.statusLabel || customer.status,
            });

      return (
        <Fragment key={customer.id}>
          <TableRow className="cursor-pointer hover:bg-muted/50" onClick={() => toggleRowExpansion(customer.id)}>
            <TableCell className="w-[30px]">
              {expandedRows.includes(customer.id) ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </TableCell>
            <TableCell>
              <div className="space-y-1">
                <div className="font-medium">{customer.name}</div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Mail className="h-3 w-3" />
                  {customer.email}
                </div>
                {customer.contactPhone ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Phone className="h-3 w-3" />
                    {customer.contactPhone}
                  </div>
                ) : null}
                {customer.companyName ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Building className="h-3 w-3" />
                    {customer.companyName}
                  </div>
                ) : null}
              </div>
            </TableCell>
            <TableCell>{renderTeamRoles(customer)}</TableCell>
            <TableCell>{getStatusBadge(customer.status, statusLabel)}</TableCell>
            <TableCell>{getTrialCell(customer, t)}</TableCell>
            <TableCell>
              <div className="text-sm text-muted-foreground">{formatDate(customer.createdAt)}</div>
              <div className="text-xs text-muted-foreground">
                {t('customers.providerList.metrics.teamsCount', { count: customer.teamsCount })}
              </div>
            </TableCell>
            <TableCell className="w-[50px]" onClick={(event) => event.stopPropagation()}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {!isDeletedView ? (
                    <>
                      <DropdownMenuItem onClick={() => navigate(`/provider/customers/${customer.id}`)}>
                        <Eye className="mr-2 h-4 w-4" />
                        {t("customers.providerList.actions.viewCustomer")}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleEdit(customer)}>
                        <Edit className="mr-2 h-4 w-4" />
                        {t("customers.providerList.actions.editCustomer")}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate(`/provider/preview?customerId=${customer.id}`)}>
                        {t("customers.providerList.actions.manageSubscriptions")}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleCheckoutLinks(customer)}>
                        <Link2 className="mr-2 h-4 w-4" />
                        {t("customers.providerList.actions.checkoutLinks")}
                      </DropdownMenuItem>
                      <DropdownMenuItem className="text-destructive" onClick={() => openDeleteDialog(customer)}>
                        <Trash2 className="mr-2 h-4 w-4" />
                        {t("customers.providerList.actions.deleteCustomer", "Archive customer")}
                      </DropdownMenuItem>
                    </>
                  ) : (
                    <DropdownMenuItem onClick={() => openRestoreDialog(customer)}>
                      <RotateCcw className="mr-2 h-4 w-4" />
                      {t("customers.providerList.actions.restoreCustomer", "Restore customer")}
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </TableCell>
          </TableRow>
          {expandedRows.includes(customer.id) ? <ExpandedRow customer={customer} t={t} /> : null}
        </Fragment>
      );
    });
  };

  return (
    <ProviderLayout>
      <div className="container mx-auto py-6 space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold">{t('customers.title')}</h1>
            <p className="text-muted-foreground">{t('customers.providerList.heading')}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={!isDeletedView ? "default" : "outline"}
              onClick={() => {
                setViewMode("active");
                setExpandedRows([]);
              }}
            >
              {t("customers.providerList.views.active", "Active")}
            </Button>
            <Button
              variant={isDeletedView ? "default" : "outline"}
              onClick={() => {
                setViewMode("deleted");
                setExpandedRows([]);
              }}
            >
              {t("customers.providerList.views.deleted", "Archived")}
            </Button>
            <Button variant="outline" onClick={() => refetch()} disabled={isFetching}>
              <RefreshCw className={isFetching ? 'mr-2 h-4 w-4 animate-spin' : 'mr-2 h-4 w-4'} />
              {t("customers.providerList.refresh")}
            </Button>
            {!isDeletedView ? (
              <Button onClick={() => setCreateDialogOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                {t("customers.providerList.addCustomer")}
              </Button>
            ) : null}
          </div>
        </div>

        {!isDeletedView ? (
          <div className="grid grid-cols-6 gap-4">
          <Card className="p-4">
            <div className="text-2xl font-bold">{stats.totalCustomers}</div>
            <div className="text-sm text-muted-foreground">
              {t('customers.providerList.stats.totalCustomers')}
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-2xl font-bold text-yellow-600">{stats.teamOwners}</div>
            <div className="text-sm text-muted-foreground">
              {t('customers.providerList.stats.teamOwners')}
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-2xl font-bold text-blue-600">{stats.teamMembers}</div>
            <div className="text-sm text-muted-foreground">
              {t('customers.providerList.stats.teamMembers')}
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-2xl font-bold text-gray-600">{stats.individuals}</div>
            <div className="text-sm text-muted-foreground">
              {t('customers.providerList.stats.individuals')}
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-2xl font-bold">{stats.uniqueTeams}</div>
            <div className="text-sm text-muted-foreground">
              {t('customers.providerList.stats.uniqueTeams')}
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-2xl font-bold text-amber-600">{stats.trialing}</div>
            <div className="text-sm text-muted-foreground">
              {t('customers.providerList.stats.trialing')}
            </div>
          </Card>
          </div>
        ) : null}

        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[220px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder={t('customers.providerList.filters.searchPlaceholder')}
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                className="pl-9"
              />
            </div>
          </div>
          <Select value={roleFilter} onValueChange={(value) => setRoleFilter(value as typeof roleFilter)}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder={t('customers.providerList.filters.allRoles')} />
            </SelectTrigger>
            <SelectContent>
              {roleOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as typeof statusFilter)}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder={t('customers.providerList.statusFallback.all')} />
            </SelectTrigger>
            <SelectContent>
              {normalizedStatusOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={teamFilter} onValueChange={setTeamFilter} disabled={teamsLoading}>
            <SelectTrigger className="w-[220px]">
              <SelectValue placeholder={t('customers.providerList.filters.teamPlaceholder')} />
            </SelectTrigger>
            <SelectContent>
              {teamOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[30px]" />
                <TableHead>{t('customers.providerList.table.customer')}</TableHead>
                <TableHead>{t('customers.providerList.table.teams')}</TableHead>
                <TableHead>{t('customers.providerList.table.status')}</TableHead>
                <TableHead>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
                    onClick={() => {
                      setTrialSort((prev) => {
                        if (!prev) return '-trial_remaining_days';
                        if (prev === '-trial_remaining_days') return 'trial_remaining_days';
                        return undefined;
                      });
                      setCreatedSort(undefined);
                    }}
                  >
                    {t('customers.providerList.table.trial')}
                    {trialSort === '-trial_remaining_days' ? (
                      <ArrowDown className="h-3 w-3" />
                    ) : trialSort === 'trial_remaining_days' ? (
                      <ArrowUp className="h-3 w-3" />
                    ) : (
                      <ArrowDownUp className="h-3 w-3 opacity-60" />
                    )}
                  </button>
                </TableHead>
                <TableHead>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
                    onClick={() => {
                      setCreatedSort((prev) => {
                        if (!prev) return '-created_at';
                        if (prev === '-created_at') return 'created_at';
                        return undefined;
                      });
                      setTrialSort(undefined);
                    }}
                  >
                    {t('customers.providerList.table.created', 'Created')}
                    {createdSort === '-created_at' ? (
                      <ArrowDown className="h-3 w-3" />
                    ) : createdSort === 'created_at' ? (
                      <ArrowUp className="h-3 w-3" />
                    ) : (
                      <ArrowDownUp className="h-3 w-3 opacity-60" />
                    )}
                  </button>
                </TableHead>
                <TableHead className="w-[50px]" />
              </TableRow>
            </TableHeader>
            <TableBody>{renderTableBody()}</TableBody>
          </Table>
        </Card>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-muted-foreground">
            {t('customers.providerList.pagination.range', {
              from,
              to,
              total: totalCount,
            })}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Select
              value={String(perPage)}
              onValueChange={(value) => {
                setPerPage(Number.parseInt(value, 10));
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder={t('customers.providerList.pagination.perPageLabel')} />
              </SelectTrigger>
              <SelectContent>
                {[10, 25, 50, 100].map((value) => (
                  <SelectItem key={value} value={String(value)}>
                    {t('customers.providerList.pagination.perPageOption', {
                      count: value,
                    })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={isFetching || currentPage <= 1}
              >
                {t('customers.providerList.pagination.previous')}
              </Button>
              <div className="text-sm text-muted-foreground">
                {t('customers.providerList.pagination.pageLabel', {
                  current: currentPage,
                  total: lastPage,
                })}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((prev) => Math.min(lastPage, prev + 1))}
                disabled={isFetching || currentPage >= lastPage}
              >
                {t('customers.providerList.pagination.next')}
              </Button>
            </div>
          </div>
        </div>

        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {deleteMode === 'hard'
                  ? t('customers.providerList.delete.titleHard', 'Delete customer permanently')
                  : t('customers.providerList.delete.titleSoft', 'Archive customer')}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {deleteTarget
                  ? t("customers.providerList.delete.description", "Choose how to remove this customer.")
                  : t("customers.providerList.delete.description", "Choose how to remove this customer.")}
              </AlertDialogDescription>
            </AlertDialogHeader>

              <div className="space-y-3">
              {deleteBlockedMeta?.subscription ? (
                <Alert>
                  <AlertDescription className="space-y-2">
                    <p className="font-medium">
                      {t(
                        'customers.providerList.delete.activeSubscriptionTitle',
                        'Permanent deletion is blocked due to an active subscription.',
                      )}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {t('customers.providerList.delete.subscriptionPlan', 'Plan')}: {deleteBlockedMeta.subscription.planName ?? '—'}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {t('customers.providerList.delete.subscriptionStatus', 'Status')}: {deleteBlockedMeta.subscription.status ?? '—'}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {t('customers.providerList.delete.subscriptionPeriodEnd', 'Current period end (UTC)')}:{' '}
                      {formatUtcDateTime(deleteBlockedMeta.subscription.currentPeriodEnd)}
                    </p>
                    {deleteBlockedMeta.allowedDeleteModes.includes('soft') ? (
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => {
                          setDeleteMode('soft');
                          setDeleteConfirm('');
                        }}
                      >
                        {t('customers.providerList.delete.switchToArchive', 'Switch to archive')}
                      </Button>
                    ) : null}
                  </AlertDescription>
                </Alert>
              ) : null}
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={deleteMode === "soft" ? "default" : "outline"}
                  onClick={() => setDeleteMode("soft")}
                >
                  {t("customers.providerList.delete.soft", "Archive")}
                </Button>
                <Button
                  type="button"
                  variant={deleteMode === "hard" ? "destructive" : "outline"}
                  onClick={() => setDeleteMode("hard")}
                >
                  {t("customers.providerList.delete.hard", "Delete permanently")}
                </Button>
              </div>

              {deleteMode === "hard" && deleteTarget ? (
                <div className="space-y-2">
                  <p className="text-sm text-destructive">
                    {t("customers.providerList.delete.hardWarning", "Permanent deletion is irreversible. Type the customer email to confirm.")}
                  </p>
                  <Input
                    value={deleteConfirm}
                    onChange={(event) => setDeleteConfirm(event.target.value)}
                    placeholder={deleteTarget.email}
                  />
                </div>
              ) : null}

              <Input
                value={deleteReason}
                onChange={(event) => setDeleteReason(event.target.value)}
                placeholder={t("customers.providerList.delete.reason", "Reason (optional)")}
              />
            </div>

            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleteMutation.isPending}>
                {t("common.cancel", "Cancel")}
              </AlertDialogCancel>
              <AlertDialogAction asChild>
                <Button
                  variant={deleteMode === "hard" ? "destructive" : "default"}
                  onClick={handleDeleteCustomer}
                  disabled={deleteMutation.isPending}
                >
                  {deleteMutation.isPending
                    ? t("customers.providerList.delete.deleting", "Deleting...")
                    : deleteMode === "hard"
                      ? t("customers.providerList.delete.confirmHard", "Delete permanently")
                      : t("customers.providerList.delete.confirmSoft", "Archive customer")}
                </Button>
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("customers.providerList.restore.title", "Restore customer")}</AlertDialogTitle>
              <AlertDialogDescription>
                {restoreTarget
                  ? t("customers.providerList.restore.description", "Restore {{email}} to active customers?", { email: restoreTarget.email })
                  : t("customers.providerList.restore.description", "Restore this customer to active customers?")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={restoreMutation.isPending}>
                {t("common.cancel", "Cancel")}
              </AlertDialogCancel>
              <AlertDialogAction asChild>
                <Button onClick={handleRestoreCustomer} disabled={restoreMutation.isPending}>
                  {restoreMutation.isPending
                    ? t("customers.providerList.restore.restoring", "Restoring...")
                    : t("customers.providerList.actions.restoreCustomer", "Restore customer")}
                </Button>
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <CreateCustomerDialog open={createDialogOpen} onOpenChange={setCreateDialogOpen} products={[]} />

        {selectedCustomer ? (
          <EditCustomerDialog
            open={editDialogOpen}
            onOpenChange={setEditDialogOpen}
            customer={mapToEditableCustomer(selectedCustomer)}
          />
        ) : null}

        {checkoutLinksCustomer ? (
          <CustomerCheckoutLinksDialog
            open={checkoutLinksDialogOpen}
            onOpenChange={setCheckoutLinksDialogOpen}
            customer={{
              id: checkoutLinksCustomer.id,
              name: checkoutLinksCustomer.name,
              email: checkoutLinksCustomer.email,
            }}
          />
        ) : null}
      </div>
    </ProviderLayout>
  );
}
